import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { QASkeleton } from '../components/QASkeleton';
import { useState } from 'react';
import type { UserRow, AccountRequestRow } from '../../../shared/api';

export function UsersPage() {
  const qc = useQueryClient();
  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ data: UserRow[] }>('/api/users'),
  });
  const { data: pending } = useQuery({
    queryKey: ['account-requests'],
    queryFn: () => api<{ data: AccountRequestRow[] }>('/api/users/account-requests'),
  });

  const [revealed, setRevealed] = useState<{ id: string; tempPwd: string } | null>(null);

  const approve = useMutation({
    mutationFn: (id: string) => api<{ userId: string; tempPassword: string }>(`/api/users/account-requests/${id}/approve`, { method: 'POST' }),
    onSuccess: (r, id) => {
      setRevealed({ id, tempPwd: r.tempPassword });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['account-requests'] });
    },
  });
  const reject = useMutation({
    mutationFn: (id: string) => api(`/api/users/account-requests/${id}/reject`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account-requests'] }),
  });

  return (
    <div>
      <div className="mb-6">
        <p className="qa-eyebrow mb-2">Users</p>
        <h1 className="qa-h1">Who has access, <span className="qa-italic-accent">and to what.</span></h1>
        <p className="qa-deck mt-2 text-ink-soft">Active users and their roles, plus the pending access-request queue.</p>
      </div>

      {(pending?.data?.length ?? 0) > 0 && (
        <section className="qa-card p-5 mb-6">
          <div className="flex items-baseline justify-between mb-3">
            <p className="qa-eyebrow">Pending access requests</p>
            <span className="qa-mono text-xs text-ink-mute">{pending!.data.length} pending</span>
          </div>
          <table className="qa-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role requested</th><th>Reason</th><th>Submitted</th><th>Actions</th></tr></thead>
            <tbody>
              {pending!.data.map((r) => (
                <tr key={r.id}>
                  <td>{r.fullName}</td>
                  <td className="qa-mono text-xs">{r.email}</td>
                  <td><span className="qa-badge qa-badge--brass">{r.requestedRole}</span></td>
                  <td className="text-xs text-ink-mute max-w-md">{r.reason || '—'}</td>
                  <td className="qa-mono text-xs">{formatDateAU(r.createdAt)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn-primary text-xs" onClick={() => approve.mutate(r.id)} disabled={approve.isPending}>Approve</button>
                      <button className="btn-danger text-xs" onClick={() => reject.mutate(r.id)} disabled={reject.isPending}>Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {revealed && (
            <div className="mt-4 bg-parchment border border-rust p-4">
              <p className="qa-eyebrow">Temporary password — shown once</p>
              <p className="qa-mono mt-2 break-all">{revealed.tempPwd}</p>
              <p className="text-xs text-ink-mute mt-2">Send this securely to the user; they should change it on first sign-in (or use Forgot password).</p>
              <button className="btn-ghost text-xs mt-2" onClick={() => setRevealed(null)}>Dismiss</button>
            </div>
          )}
        </section>
      )}

      {isLoading ? <QASkeleton variant="table" /> : (
        <div className="overflow-x-auto border border-hairline">
          <table className="qa-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Last login</th></tr></thead>
            <tbody>
              {(users?.data ?? []).map((u) => (
                <tr key={u.id}>
                  <td>{u.fullName}</td>
                  <td className="qa-mono text-xs">{u.email}</td>
                  <td><span className="qa-badge qa-badge--brass">{u.role}</span></td>
                  <td>{u.active ? <span className="qa-badge qa-badge--pass">YES</span> : <span className="qa-badge qa-badge--out">NO</span>}</td>
                  <td className="qa-mono text-xs">{u.lastLoginAt ? formatDateAU(u.lastLoginAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
